import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/site/Layout";
import { scrollToTarget } from "@/components/site/motion";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import Home from "@/pages/Home";
import PlanDetail from "@/pages/PlanDetail";
import Quote from "@/pages/Quote";
import Contact from "@/pages/Contact";
import NotFound from "@/pages/NotFound";
import CityPage from "@/pages/CityPage";

// Code-split rarely-visited routes so the initial bundle stays lean.
// Recharts (used by Dashboard) alone is ~200KB — kept out of the public bundle.
const DentalSearch = lazy(() => import("@/pages/DentalSearch"));
const Medicare101 = lazy(() => import("@/pages/Medicare101"));
const ShortTermMedical = lazy(() => import("@/pages/ShortTermMedical"));
const AdminLogin = lazy(() => import("@/pages/admin/Login"));
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const Clients = lazy(() => import("@/pages/admin/Clients"));
const ClientForm = lazy(() => import("@/pages/admin/ClientForm"));
const Dentists = lazy(() => import("@/pages/admin/Dentists"));
const ForgotPassword = lazy(() => import("@/pages/admin/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/admin/ResetPassword"));

const RouteFallback = () => (
  <div className="min-h-[50vh] flex items-center justify-center">
    <div className="w-6 h-6 rounded-full border-2 border-slate-300 border-t-[var(--brand-primary)] animate-spin" />
  </div>
);

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const t = setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) scrollToTarget(el);
      }, 120);
      return () => clearTimeout(t);
    }
    scrollToTarget(0, { immediate: true });
    return undefined;
  }, [pathname, hash]);
  return null;
};

const PublicRoutes = () => (
  <Layout>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/aca-health" element={<PlanDetail slug="aca-health" />} />
      <Route path="/medicare-advantage" element={<PlanDetail slug="medicare-advantage" />} />
      <Route path="/medicare-supplement" element={<PlanDetail slug="medicare-supplement" />} />
      <Route path="/dental" element={<PlanDetail slug="dental" />} />
      <Route path="/dental/search" element={<DentalSearch />} />
      <Route path="/hospital-indemnity" element={<PlanDetail slug="hospital-indemnity" />} />
      <Route path="/cancer-plans" element={<PlanDetail slug="cancer-plans" />} />
      <Route path="/long-term-care" element={<PlanDetail slug="long-term-care" />} />
      <Route path="/final-expense" element={<PlanDetail slug="final-expense" />} />
      <Route path="/plan/:slug" element={<PlanDetail />} />
      <Route path="/quote" element={<Quote />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/medicare-101" element={<Medicare101 />} />
      <Route path="/short-term-medical" element={<ShortTermMedical />} />
      <Route path="/santa-rosa-beach-health-insurance" element={<CityPage />} />
      <Route path="/destin-medicare-broker" element={<CityPage />} />
      <Route path="/miramar-beach-health-insurance" element={<CityPage />} />
      <Route path="/panama-city-beach-health-insurance" element={<CityPage />} />
      <Route path="/freeport-health-insurance" element={<CityPage />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Layout>
);

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Admin routes (no public Layout) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients"
            element={
              <ProtectedRoute>
                <Clients />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients/new"
            element={
              <ProtectedRoute>
                <ClientForm />
              </ProtectedRoute>
            }
          />
          <Route path="/admin/forgot-password" element={<ForgotPassword />} />
          <Route path="/admin/reset-password" element={<ResetPassword />} />
          <Route
            path="/admin/dentists"
            element={
              <ProtectedRoute>
                <Dentists />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clients/:id"
            element={
              <ProtectedRoute>
                <ClientForm />
              </ProtectedRoute>
            }
          />

          {/* Everything else uses the public site layout */}
          <Route path="/*" element={<PublicRoutes />} />
        </Routes>
        </Suspense>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
